const escapeToken = (token: string) => token.replace(/~/g, '~0').replace(/\//g, '~1');

/** Map every JSON pointer in ``text`` to the character span of its value. */
export const jsonPointerSpans = (text: string): Map<string, [number, number]> | null => {
  const spans = new Map<string, [number, number]>();
  let i = 0;

  const skipWhitespace = () => {
    while (i < text.length && /\s/.test(text[i])) i += 1;
  };

  const expect = (char: string) => {
    skipWhitespace();
    if (text[i] !== char) throw new Error(`Expected ${char} at ${i}`);
    i += 1;
  };

  const parseString = (): string => {
    skipWhitespace();
    const start = i;
    if (text[i] !== '"') throw new Error(`Expected string at ${i}`);
    i += 1;
    while (i < text.length && text[i] !== '"') {
      i += text[i] === '\\' ? 2 : 1;
    }
    i += 1;
    return JSON.parse(text.slice(start, i)) as string;
  };

  const parseValue = (pointer: string) => {
    skipWhitespace();
    const start = i;
    const char = text[i];
    if (char === '{') {
      i += 1;
      skipWhitespace();
      if (text[i] === '}') {
        i += 1;
      } else {
        for (;;) {
          const key = parseString();
          expect(':');
          parseValue(`${pointer}/${escapeToken(key)}`);
          skipWhitespace();
          if (text[i] === ',') {
            i += 1;
            continue;
          }
          expect('}');
          break;
        }
      }
    } else if (char === '[') {
      i += 1;
      skipWhitespace();
      if (text[i] === ']') {
        i += 1;
      } else {
        for (let index = 0; ; index += 1) {
          parseValue(`${pointer}/${index}`);
          skipWhitespace();
          if (text[i] === ',') {
            i += 1;
            continue;
          }
          expect(']');
          break;
        }
      }
    } else if (char === '"') {
      parseString();
    } else {
      while (i < text.length && /[^\s,\]}]/.test(text[i])) i += 1;
      if (i === start) throw new Error(`Unexpected token at ${i}`);
    }
    spans.set(pointer, [start, i]);
  };

  try {
    JSON.parse(text);
    parseValue('');
    return spans;
  } catch {
    return null;
  }
};

/** Resolve the span for ``pointer``, falling back to the nearest existing parent. */
export const locatePointer = (text: string, pointer: string): [number, number] | null => {
  const spans = jsonPointerSpans(text);
  if (!spans) return null;
  let current = pointer;
  for (;;) {
    const span = spans.get(current);
    if (span) return span;
    if (current === '') return null;
    current = current.slice(0, current.lastIndexOf('/'));
  }
};

export const lineOfOffset = (text: string, offset: number): number =>
  text.slice(0, offset).split('\n').length;
